import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Download } from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { SkeletonCard, SkeletonTable } from '@/components/ui/StateComponents.jsx';
import { exportExecutiveDashboardStub, fetchExecutiveDashboard } from '@/services/executive-dashboard.js';

const money = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });

const KPI_CONFIG = [
  { key: 'total_facturado', label: 'Facturación' },
  { key: 'total_costos', label: 'Costos' },
  { key: 'margen_total', label: 'Margen' },
  { key: 'cartera_por_cobrar', label: 'Cartera por cobrar' },
  { key: 'cartera_vencida', label: 'Vencido' },
  { key: 'liquidaciones_total_pendiente', label: 'Liquidaciones pendientes' },
  { key: 'sla_vencidas', label: 'SLA vencidas', operational: true },
  { key: 'guias_retenidas', label: 'Retenidas', operational: true },
];

const rankingTabs = [
  { id: 'top_clientes_rentables', label: 'Clientes rentables' },
  { id: 'top_lineas_rentables', label: 'Líneas rentables' },
  { id: 'top_clientes_morosos', label: 'Clientes morosos' },
  { id: 'guias_con_margen_negativo', label: 'Guías con margen negativo' },
];

export default function ExecutiveDashboardPage() {
  const [filters, setFilters] = useState({ desde: '', hasta: '', sucursal_id: '', comparar_con_periodo_anterior: true });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fullWidth, setFullWidth] = useState(false);
  const [activeTab, setActiveTab] = useState('top_clientes_rentables');
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const dashboard = await fetchExecutiveDashboard({
        ...filters,
        sucursal_id: filters.sucursal_id ? Number(filters.sucursal_id) : null,
      });
      setData(dashboard);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el panel ejecutivo.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cards = useMemo(() => {
    const fin = data?.kpis_financieros || {};
    const op = data?.kpis_operativos || {};
    const cmp = data?.comparativo || {};
    return KPI_CONFIG.map((item) => {
      const raw = item.operational ? op[item.key] : fin[item.key];
      const numeric = Number(raw || 0);
      const variation = item.key === 'total_facturado'
        ? cmp.total_facturado_pct
        : item.key === 'margen_total'
          ? cmp.margen_total_pct
          : item.key === 'cartera_vencida'
            ? cmp.cartera_vencida_pct
            : item.key === 'sla_vencidas'
              ? cmp.sla_vencidas_pct
              : item.key === 'guias_retenidas'
                ? cmp.guias_entregadas_pct
                : 0;
      return {
        ...item,
        value: item.operational ? String(numeric) : money.format(numeric),
        variation: Number(variation || 0),
      };
    });
  }, [data]);

  const rankingRows = useMemo(() => {
    const list = data?.rankings?.[activeTab] || [];
    return list.filter((row) => JSON.stringify(row).toLowerCase().includes(query.toLowerCase()));
  }, [data, activeTab, query]);

  const chartData = useMemo(() => {
    const fx = data?.series?.facturacion_diaria || [];
    const cx = data?.series?.costos_diarios || [];
    const mx = data?.series?.margen_diario || [];
    return fx.map((row, index) => ({
      fecha: row.fecha,
      facturacion: row.total,
      costos: cx[index]?.total || 0,
      margen: mx[index]?.total || 0,
    }));
  }, [data]);

  const alerts = useMemo(() => {
    const morosos = (data?.rankings?.top_clientes_morosos || []).slice(0, 4).map((item) => ({
      tipo: 'MOROSIDAD',
      label: `${item.nombre}: ${money.format(item.saldo_pendiente)}`,
      href: `/clientes/${item.id}`,
    }));
    const negativos = (data?.rankings?.guias_con_margen_negativo || []).slice(0, 6).map((item) => ({
      tipo: 'MARGEN',
      label: `Guía #${item.guia_id} · ${money.format(item.margen)}`,
      href: `/guias/${item.guia_id}`,
    }));
    return [...morosos, ...negativos].slice(0, 10);
  }, [data]);

  return (
    <section className={`w-full px-6 py-6 text-[rgb(var(--fg))] ${fullWidth ? '' : 'mx-auto max-w-[1400px]'}`}>
      <header className="grid grid-cols-12 gap-3 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.95)] p-4">
        <div className="col-span-12 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Panel Ejecutivo</h1>
          <div className="flex items-center gap-2">
            <button type="button" className="rounded-xl border border-[rgba(var(--border),0.7)] px-3 py-2" onClick={() => setFullWidth((v) => !v)}>{fullWidth ? 'Centrado' : 'Full width'}</button>
            <button type="button" className="inline-flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-3 py-2 text-[rgb(var(--fg))]" onClick={() => exportExecutiveDashboardStub()}>
              <Download size={16} /> Exportar
            </button>
          </div>
        </div>
        <label className="col-span-12 md:col-span-3">Desde
          <input type="date" className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--subpanel),0.65)] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]" value={filters.desde} onChange={(e) => setFilters((prev) => ({ ...prev, desde: e.target.value }))} />
        </label>
        <label className="col-span-12 md:col-span-3">Hasta
          <input type="date" className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--subpanel),0.65)] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]" value={filters.hasta} onChange={(e) => setFilters((prev) => ({ ...prev, hasta: e.target.value }))} />
        </label>
        <label className="col-span-12 md:col-span-3">Sucursal
          <input type="number" className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--subpanel),0.65)] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]" value={filters.sucursal_id} onChange={(e) => setFilters((prev) => ({ ...prev, sucursal_id: e.target.value }))} />
        </label>
        <div className="col-span-12 md:col-span-3 flex items-end">
          <button type="button" className="w-full rounded-xl border border-[rgba(var(--border),0.7)] px-3 py-2" onClick={load}>Aplicar filtros</button>
        </div>
      </header>

      {error ? <div className="mt-4 rounded-xl border border-[rgba(var(--danger),0.35)] bg-[rgba(var(--danger),0.1)] p-3 text-sm">{error}</div> : null}

      <section className="mt-4">
        <h2 className="mb-3 text-lg font-semibold">Resumen Hoy / Rango</h2>
        {loading ? (
          <div className="grid grid-cols-12 gap-3">{Array.from({ length: 8 }).map((_, index) => <div key={index} className="col-span-12 md:col-span-3"><SkeletonCard /></div>)}</div>
        ) : (
          <div className="grid grid-cols-12 gap-3">
            {cards.map((card) => (
              <article key={card.key} className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.93)] p-4 md:col-span-3">
                <p className="text-sm text-[rgba(var(--muted),0.9)]">{card.label}</p>
                <p className="mt-2 text-2xl font-semibold">{card.value}</p>
                <p className={`mt-2 text-sm ${card.variation >= 0 ? 'text-[rgb(var(--success))]' : 'text-[rgb(var(--danger))]'}`}>{card.variation >= 0 ? '↑' : '↓'} {Math.abs(card.variation)}%</p>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="mt-6 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.93)] p-4">
        <h2 className="mb-3 text-lg font-semibold">Gráficos</h2>
        {loading ? <SkeletonCard /> : (
          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid stroke="rgba(var(--border),0.35)" />
                <XAxis dataKey="fecha" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="facturacion" stroke="rgb(var(--brand))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="costos" stroke="rgb(var(--warning))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="margen" stroke="rgb(var(--success))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      <section className="mt-6 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.93)] p-4">
        <h2 className="mb-3 text-lg font-semibold">Rankings</h2>
        <div className="mb-3 flex flex-wrap gap-2">
          {rankingTabs.map((tab) => (
            <button key={tab.id} type="button" aria-current={activeTab === tab.id ? 'page' : undefined} className={`rounded-xl px-3 py-2 ${activeTab === tab.id ? 'bg-[rgb(var(--brand))]' : 'border border-[rgba(var(--border),0.7)]'}`} onClick={() => setActiveTab(tab.id)}>{tab.label}</button>
          ))}
          <input aria-label="Buscar en ranking" placeholder="Buscar" className="ml-auto rounded-xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--subpanel),0.65)] px-3 py-2" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        {loading ? <SkeletonTable rows={5} columns={6} /> : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead><tr><th className="text-left">Entidad</th><th className="text-right">Facturado</th><th className="text-right">Costos</th><th className="text-right">Margen</th><th className="text-right">Saldo</th><th className="text-right">Acción</th></tr></thead>
              <tbody>
                {rankingRows.map((row) => (
                  <tr key={row.id || row.guia_id} className="border-t border-[rgba(var(--border),0.35)]">
                    <td className="py-2">{row.nombre || `Guía #${row.guia_id}`}</td>
                    <td className="py-2 text-right">{money.format(row.total_facturado || row.facturado || 0)}</td>
                    <td className="py-2 text-right">{money.format(row.total_costos || row.costos || 0)}</td>
                    <td className="py-2 text-right">{money.format(row.margen_total || row.margen || 0)}</td>
                    <td className="py-2 text-right">{money.format(row.saldo_pendiente || 0)}</td>
                    <td className="py-2 text-right">
                      <Link to={row.guia_id ? `/guias/${row.guia_id}` : `/clientes/${row.id}`} className="rounded-lg border border-[rgba(var(--border),0.7)] px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]">Abrir</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mt-6 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.93)] p-4">
        <h2 className="mb-3 text-lg font-semibold">Alertas críticas</h2>
        {loading ? <SkeletonTable rows={4} columns={3} /> : (
          <div className="space-y-2">
            {alerts.length === 0 ? <p className="text-sm text-[rgba(var(--muted),0.85)]">Sin alertas críticas para el rango.</p> : alerts.map((alert, index) => (
              <div key={`${alert.tipo}-${index}`} className="flex items-center justify-between rounded-xl border border-[rgba(var(--border),0.5)] p-3">
                <div className="inline-flex items-center gap-2"><AlertTriangle size={16} /> <span>{alert.tipo}</span> · <span>{alert.label}</span></div>
                <div className="flex items-center gap-2">
                  <Link to={alert.href} className="rounded-lg border border-[rgba(var(--border),0.7)] px-2 py-1">Abrir entidad</Link>
                  <button type="button" className="rounded-lg border border-[rgba(var(--border),0.7)] px-2 py-1">Snooze</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </section>
  );
}
